import React from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Box,
  ButtonGroup,
  Icon,
  Text,
} from "@chakra-ui/react";
import { ColumnType, TimeBucket, timeBucketToJSON, AggregationType, aggregationTypeToJSON } from "build-ts/enums";
import { AppIcons } from "constants/icons";
import { iconForKgColumn } from "modules/mkg/utils/ontologyEntityIcons";

const MEASURE_AGGREGATION_OPTIONS: AggregationType[] = [
  AggregationType.SUM,
  AggregationType.AVERAGE,
  AggregationType.COUNT,
  AggregationType.MIN,
  AggregationType.MAX,
];

interface KGColumnCreationFormProps {
  colType: ColumnType;
  name: string;
  description: string;
  lowCardinality: boolean;
  timeBucket: TimeBucket;
  aggregation: AggregationType;
  isSubmitting: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onLowCardinalityChange: (value: boolean) => void;
  onTimeBucketChange: (value: TimeBucket) => void;
  onAggregationChange: (value: AggregationType) => void;
  onSubmit: () => void;
  onClose: () => void;
}

const KGColumnCreationForm: React.FC<KGColumnCreationFormProps> = ({
  colType,
  name,
  description,
  lowCardinality,
  timeBucket,
  aggregation,
  isSubmitting,
  onNameChange,
  onDescriptionChange,
  onLowCardinalityChange,
  onTimeBucketChange,
  onAggregationChange,
  onSubmit,
  onClose,
}) => {
  return (
    <Box p={4}>
      <VStack spacing={4} align="stretch">
        <HStack
          spacing={3}
          px={3}
          py={2.5}
          bg="purple.50"
          borderRadius="md"
          borderWidth="1px"
          borderColor="purple.100"
          align="center"
        >
          <Icon as={iconForKgColumn(colType)} boxSize={5} color="purple.600" flexShrink={0} aria-hidden />
          <Box minW={0}>
            <Text
              fontSize="xs"
              fontWeight="700"
              color="purple.700"
              letterSpacing="wider"
              mb={0}
              textTransform="uppercase"
            >
              Entity type
            </Text>
            <Text fontWeight="600" fontSize="sm" color="gray.800" mb={0}>
              {colType === ColumnType.MEASURE ? "Measure" : colType === ColumnType.TEMPORAL ? "Time" : "Attribute"}
            </Text>
          </Box>
        </HStack>

        <FormControl isRequired>
          <FormLabel>Name</FormLabel>
          <Input placeholder="Enter name" value={name} onChange={e => onNameChange(e.target.value)} />
        </FormControl>

        <FormControl>
          <FormLabel>Description</FormLabel>
          <Input
            placeholder="Enter description"
            value={description}
            onChange={e => onDescriptionChange(e.target.value)}
          />
        </FormControl>

        {colType === ColumnType.ATTRIBUTE && (
          <FormControl>
            <FormLabel>Cardinality</FormLabel>
            <ButtonGroup size="sm" isAttached variant="outline">
              <Button
                colorScheme={lowCardinality ? "purple" : "gray"}
                variant={lowCardinality ? "solid" : "outline"}
                onClick={() => onLowCardinalityChange(true)}
              >
                Low
              </Button>
              <Button
                colorScheme={!lowCardinality ? "purple" : "gray"}
                variant={!lowCardinality ? "solid" : "outline"}
                onClick={() => onLowCardinalityChange(false)}
              >
                High
              </Button>
            </ButtonGroup>
          </FormControl>
        )}

        {colType === ColumnType.MEASURE && (
          <FormControl>
            <FormLabel>Default Aggregation</FormLabel>
            <Select
              value={aggregationTypeToJSON(aggregation)}
              onChange={e => {
                const found = MEASURE_AGGREGATION_OPTIONS.find(a => aggregationTypeToJSON(a) === e.target.value);
                if (found !== undefined) onAggregationChange(found);
              }}
            >
              {MEASURE_AGGREGATION_OPTIONS.map(a => (
                <option key={aggregationTypeToJSON(a)} value={aggregationTypeToJSON(a)}>
                  {aggregationTypeToJSON(a)}
                </option>
              ))}
            </Select>
          </FormControl>
        )}

        {colType === ColumnType.TEMPORAL && (
          <FormControl>
            <FormLabel>Time Bucket</FormLabel>
            <Select
              value={timeBucketToJSON(timeBucket)}
              onChange={e => {
                const entries: [string, TimeBucket][] = [
                  [timeBucketToJSON(TimeBucket.DAILY), TimeBucket.DAILY],
                  [timeBucketToJSON(TimeBucket.WEEKLY), TimeBucket.WEEKLY],
                  [timeBucketToJSON(TimeBucket.MONTHLY), TimeBucket.MONTHLY],
                  [timeBucketToJSON(TimeBucket.QUARTERLY), TimeBucket.QUARTERLY],
                  [timeBucketToJSON(TimeBucket.YEARLY), TimeBucket.YEARLY],
                ];
                const found = entries.find(([k]) => k === e.target.value);
                if (found) onTimeBucketChange(found[1]);
              }}
            >
              <option value={timeBucketToJSON(TimeBucket.DAILY)}>DAILY</option>
              <option value={timeBucketToJSON(TimeBucket.WEEKLY)}>WEEKLY</option>
              <option value={timeBucketToJSON(TimeBucket.MONTHLY)}>MONTHLY</option>
              <option value={timeBucketToJSON(TimeBucket.QUARTERLY)}>QUARTERLY</option>
              <option value={timeBucketToJSON(TimeBucket.YEARLY)}>YEARLY</option>
            </Select>
          </FormControl>
        )}

        <HStack spacing={3} pt={4} justify="center" w="100%">
          <Button
            colorScheme="green"
            bg="green.600"
            color="white"
            _hover={{ bg: "green.700" }}
            onClick={onSubmit}
            isLoading={isSubmitting}
            size="md"
            leftIcon={<Icon as={AppIcons.save} boxSize={5} />}
            w="50%"
          >
            <Text mb={0} fontWeight="700">
              Create
            </Text>
          </Button>
          <Button
            onClick={onClose}
            size="md"
            variant="outline"
            colorScheme="purple"
            leftIcon={<Icon as={AppIcons.close} boxSize={5} />}
            w="50%"
          >
            <Text mb={0} fontWeight="700">
              Cancel
            </Text>
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default KGColumnCreationForm;
